import React, { useRef } from 'react';
import { Draggable } from 'react-beautiful-dnd';
import { Constants } from '@pwc/pwc-deals-web-common';
import { TABLEAU_LIBRARY_ITEM_TYPES } from 'common/components/SectionArea/libraryItemUtils.ts';
import { topicPanelType } from 'features/project/ViewProject/ProjectOverview/Topic/mapTopic';
import { isTableauPanel } from 'features/project/ViewProject/ProjectOverview/Topic/topicUtils.ts';
import UnableToLoadContent from 'features/analyses/common/UnableToLoadContent/UnableToLoadContent';
import TopicLibraryItemCard from 'common/components/SectionArea/LibraryItemCard/TopicLibraryItemCard';
import { isPowerPivotLibraryItem } from 'features/analyses/common/LibraryItemCard/PowerPivot/constants/PowerPivotTypes.ts';
import PowerPivotLibraryItemCard from 'features/analyses/common/LibraryItemCard/PowerPivot/components/PowerPivotLibraryItemCard.tsx';
import {
  getIsPowerBiReport,
  getPowerBIPageNameFromTopic,
} from '../../../features/project/ViewProject/ProjectOverview/utils/helper';
import PowerBiReport from '../../../features/analyses/PowerBiReport/PowerBiReport';
import LibraryItemCard from './LibraryItemCard/LibraryItemCard';
import RichTextLibraryItemCard from './LibraryItemCard/RichTextLibraryItemCard';
import './DraggableLibraryItemCard.scss';
import ExcelPPTLibraryItemCard from '../../../features/analyses/common/LibraryItemCard/ExcelPPTLibraryItemCard';
import { isTableauLibraryItem } from './libraryItemUtils.ts';
import { TableauPreview } from '../SelectReport/TableauPreview.tsx';

// disable all react-beautiful-dnd development warnings
window['__react-beautiful-dnd-disable-dev-warnings'] = true;

/** Good to know :D
 *
 * public enum TopicPanelType
 * {
 *  Undefined = 0,
 *  RichText = 1,
 *  Hypothesis = 2,
 *  ExcelChart = 3,
 *  ExcelNamedRange = 4,
 *  ExcelCellRange = 5,
 *  PowerBiReport = 6,
 *  Powerpoint = 7,
 *  PowerBiReportPage = 8,
 *  TableauReport = 9,
 *  TableauReportPage = 10,
 * }
 * public enum LibraryItemType
 * {
 *  Undefined = 0,
 *  RichText = 100,
 *  ExcelChart = 300,
 *  ExcelNamedRange = 301,
 *  ExcelCellRange = 302,
 *  PowerBiReport = 400,
 *  PowerBiReportPage = 401,
 *  PowerpointGroup = 500,
 *  File = 600
 *  TableauReport = 700
 *  TableauReportPage = 701
 * }
 */
export const renderableContentTopic = [
  // Topics
  Constants.TOPIC_PANEL_TYPES.KEY_FOCUS_AREA.value,
  Constants.TOPIC_PANEL_TYPES.EXCEL_CHART.value,
  4,
  5,
  7,
];
export const renderableContentTypesFreeform = [
  // Library
  Constants.LIBRARY_ITEM_TYPES.EXCEL_NAMED_RANGE.value,
  Constants.LIBRARY_ITEM_TYPES.EXCEL_CHART.value,
  Constants.LIBRARY_ITEM_TYPES.EXCEL_CELL_RANGE.value,
  Constants.LIBRARY_ITEM_TYPES.POWER_POINT_GROUP.value,
];

const DraggableLibraryItemCard = ({
  panel,
  topic,
  panelNumber,
  onRichTextContentUpdate,
  projectId,
  excelPPTItem,
  onFormatTypeChange,
  dynamicExcelObject,
  panels,
}) => {
  const ref = useRef();
  const memoizedLibraryItem = useRef(dynamicExcelObject || panel);
  const { libraryItemId = panelNumber } = panel;
  const isPowerBiReport = getIsPowerBiReport(panel);

  const predefinedPageName = getPowerBIPageNameFromTopic(topic, panel);
  const figureElement = document?.querySelector(
    '.ck-widget_with-selection-handle'
  );
  if (figureElement) {
    figureElement.setAttribute('contenteditable', 'true');
  }
  return (
    <>
      <Draggable
        key={libraryItemId}
        draggableId={libraryItemId?.toString()}
        index={panelNumber - 1}
        isDragDisabled={false}
      >
        {(provided, draggableSnapshot) => {
          return (
            // eslint-disable-next-line jsx-a11y/no-static-element-interactions
            <div
              className="draggable-library-item-card2"
              data-testid="draggable-library-item-card"
              {...provided.draggableProps}
              {...provided.dragHandleProps}
              ref={(myRef) => {
                provided.innerRef(myRef);
              }}
            >
              {panel.error &&
              panel.error.isError &&
              (excelPPTItem || dynamicExcelObject) ? (
                <UnableToLoadContent
                  apiErrorCode={panel.error.message?.apiErrorCode}
                  errorCode={panel.error.message?.errorCode}
                  type={panel.statusType || excelPPTItem.type}
                  data={{
                    sharePointItemUrl: excelPPTItem.sharePointItemUrl,
                    worksheetName:
                      excelPPTItem?.autoSync?.data.worksheetName ||
                      excelPPTItem?.worksheetName,
                    chartName:
                      excelPPTItem.autoSync &&
                      excelPPTItem.autoSync.data.chartName,
                    groupName: excelPPTItem.groupName,
                    name:
                      excelPPTItem.autoSync && excelPPTItem.autoSync.data.name,
                  }}
                  projectId={projectId}
                />
              ) : (
                <>
                  {panel.type ===
                    Constants.TOPIC_PANEL_TYPES.RICH_TEXT.value && (
                    <RichTextLibraryItemCard
                      panel={panel}
                      panelNumber={panelNumber}
                      setIsDragDisabled={() => {}}
                      isDragging={draggableSnapshot.isDragging}
                      onRichTextContentUpdate={onRichTextContentUpdate}
                      panels={panels}
                      libraryItemId
                    />
                  )}
                  {isPowerPivotLibraryItem(dynamicExcelObject?.type) && (
                    <LibraryItemCard innerRef={ref}>
                      <PowerPivotLibraryItemCard
                        libraryItem={{
                          ...memoizedLibraryItem.current,
                          data: JSON.stringify(
                            memoizedLibraryItem.current.data
                          ),
                        }}
                        projectId={projectId}
                        setIsDragDisabled={draggableSnapshot.isDragging}
                      />
                    </LibraryItemCard>
                  )}
                  {renderableContentTopic.includes(panel.type) && (
                    <LibraryItemCard innerRef={ref}>
                      <div>
                        <TopicLibraryItemCard
                          content={panel.content || panel.sharedContent}
                          excelPPTItem={excelPPTItem}
                          panel={panel}
                          projectId={projectId}
                          onFormatTypeChange={onFormatTypeChange}
                        />
                      </div>
                    </LibraryItemCard>
                  )}
                  {renderableContentTypesFreeform.includes(panel.type) && (
                    <LibraryItemCard innerRef={ref}>
                      <ExcelPPTLibraryItemCard
                        libraryItem={memoizedLibraryItem.current}
                        projectId={projectId}
                        setIsDragDisabled={() => {}}
                        onFormatTypeChange={(
                          libItemId,
                          formatType,
                          libraryItemsSvgHtml
                        ) => {
                          const htmlObject = libraryItemsSvgHtml.HTML;
                          const svgObject = libraryItemsSvgHtml.SVG;
                          const updatedPanel = {
                            ...panel,
                            autoSync: htmlObject.autoSync,
                            contentsWithFormatType: [
                              {
                                ...htmlObject,
                                content: htmlObject.value,
                                value: undefined,
                              },
                              {
                                ...svgObject,
                                content: svgObject.value,
                                value: undefined,
                              },
                            ],
                          };
                          onFormatTypeChange(
                            updatedPanel,
                            libItemId,
                            formatType,
                            false
                          );
                        }}
                      />
                    </LibraryItemCard>
                  )}
                  {isPowerBiReport && (
                    <PowerBiReport
                      item={panel}
                      projectId={projectId}
                      predefinedPageName={predefinedPageName}
                    />
                  )}
                  {(isTableauLibraryItem(panel.type) ||
                    isTableauPanel(panel.type)) && (
                    <TableauPreview
                      report={{
                        ...panel,
                        tabName: panel.data.tabName,
                        name: panel.name || panel.data.name,
                      }}
                      hideTabs={
                        panel.type ===
                          TABLEAU_LIBRARY_ITEM_TYPES.TableauReportPage ||
                        panel.type === topicPanelType.TableauReportPage
                      }
                    />
                  )}
                </>
              )}
            </div>
          );
        }}
      </Draggable>
    </>
  );
};

export default DraggableLibraryItemCard;
